// Content selection follows HomeScreen and SearchScreen. No locally invented ratings/reasons.
export const uniqueBy = (items, key) =>
  items.filter(
    (item, index) => item && items.findIndex((other) => other?.[key] === item[key]) === index,
  );
const albumReasons = {
  RECENT_RELEASE: 'home.discoveryReason.recentRelease',
  FOLLOWED_REVIEW: 'home.discoveryReason.followedReview',
  SAVED_NEW_REVIEW: 'home.discoveryReason.savedNewReview',
  WEEKLY_REVIEW_GROWTH: 'home.discoveryReason.weeklyGrowth',
  HIGH_RATING: 'search.reason.highRating',
  INTERESTED_ARTIST: 'search.reason.interestedArtist',
  INTERESTED_GENRE: 'search.reason.interestedGenre',
  INTERESTED_ERA: 'search.reason.interestedEra',
  SIMILAR_LISTENERS: 'search.reason.similarListeners',
  CO_LIKED_ALBUM: 'search.reason.coLiked',
  POSTPOP_RECOMMENDATION: 'search.postpopPick',
  GENERAL_KOREAN: 'search.koreanMusic',
  GENERAL_GLOBAL: 'search.internationalMusic',
};
export function discoveryReason(item, t, locale) {
  if (item.fallback && item.homeReasonKey) return t(item.homeReasonKey);
  if (item.reason === 'RELEASE_ANNIVERSARY') {
    const years = item.reasonLabel?.match(/(\d+)\s*(?:주년|years?)/)?.[1];
    return t(years ? 'search.reason.anniversary' : 'search.reason.anniversaryGeneric', { years });
  }
  if (item.reason === 'INTERESTED_REGION')
    return t(
      item.displayRegion === 'KR'
        ? 'search.reason.interestedDomestic'
        : item.displayRegion === 'GLOBAL'
          ? 'search.reason.interestedInternational'
          : 'search.reason.interestedRegion',
    );
  return albumReasons[item.reason]
    ? t(albumReasons[item.reason])
    : (locale === 'ko' && item.reasonLabel) || t('search.worthExploring');
}
export function trackReason(item, t, locale) {
  if (item.reason === 'COMMUNITY_TOP_TRACK' && item.averageRating != null && item.ratingCount)
    return t('search.reason.communityTopTrackWithRating', {
      rating: Number(item.averageRating).toFixed(1),
      count: item.ratingCount,
    });
  if (item.reason === 'SIMILAR_LISTENERS_TRACK')
    return t(
      item.reasonEvidenceCount
        ? 'home.trackReason.similarListenersWithCount'
        : 'home.trackReason.similarListeners',
      { count: item.reasonEvidenceCount },
    );
  const keys = {
    INTERESTED_ARTIST_TRACK: 'interestedArtist',
    INTERESTED_GENRE_TRACK: 'interestedGenre',
    REPRESENTATIVE_TRACK: 'representative',
    COMMUNITY_TOP_TRACK: 'communityTop',
    RECENTLY_RISING_TRACK: 'recentlyRising',
    EARLY_RESPONSE_TRACK: 'earlyResponse',
  };
  if (item.reason === 'POSTPOP_PICK_TRACK') return t('search.reason.postpopPickTrack');
  return keys[item.reason]
    ? t(`home.trackReason.${keys[item.reason]}`)
    : (locale === 'ko' && item.reasonLabel) || t('search.worthExploring');
}
export function searchContent(data = {}, home = {}) {
  const fallback = [
    [home.koreanMusicReviews?.[0], 'GENERAL_KOREAN'],
    [home.globalMusicReviews?.[0], 'GENERAL_GLOBAL'],
    [home.recommendedReviews?.[1], 'POSTPOP_RECOMMENDATION'],
    [home.featuredReviews?.[1], 'GENERAL_DISCOVERY'],
    [home.latestReviews?.[0], 'GENERAL_DISCOVERY', true],
    [home.latestReviews?.[1], 'GENERAL_DISCOVERY', true],
  ]
    .filter(([item]) => item)
    .map(([item, reason, openReview]) => ({
      ...item,
      id: item.albumId,
      reason,
      openReview,
      fallback: true,
    }));
  const albums = uniqueBy([...(data.albums || []), ...fallback], 'id').filter(
    (item) => !(data.excludedAlbumIds || []).includes(item.id),
  );
  const pick = albums.find((item) => item.reason === 'POSTPOP_RECOMMENDATION');
  const others = albums.filter((item) => item !== pick);
  return {
    albums: (pick ? [...others.slice(0, 2), pick, ...others.slice(2)] : others).slice(0, 4),
    reviews: uniqueBy(
      [
        ...(data.reviews || []).map((item) => ({ ...item.review, reason: item.reason })),
        ...(home.latestReviews || []),
      ],
      'reviewId',
    ).slice(0, 2),
    playlists: uniqueBy(
      [
        ...(data.playlists || []).map((item) => ({ ...item.playlist, reason: item.reason })),
        ...(home.latestLists || []),
      ],
      'id',
    ).slice(0, 3),
  };
}
export function dividedReviews(detail) {
  const reviews = [...(detail?.reviews || [])]
    .filter(
      (item) =>
        Boolean(item.content?.replace(/[\s\u200B-\u200D\uFEFF]/gu, '')) &&
        Number.isFinite(Number(item.rating)),
    )
    .sort((a, b) => Number(b.rating) - Number(a.rating));
  const high = reviews[0];
  const low = [...reviews].reverse().find((item) => item.user?.id !== high?.user?.id);
  return high && low && Number(high.rating) - Number(low.rating) >= 2 ? { high, low } : null;
}
const hasReviewText = (item) => Boolean(item.content?.replace(/[\s\u200B-\u200D\uFEFF]/gu, ''));
export function dividedCandidates(home = {}, rotation = 0) {
  // Match build60's Map: keep the last review for each album, in first-seen order.
  const explicit = Array.from(
    new Map(
      (home.dividedOpinionReviews || []).filter(hasReviewText).map((item) => [item.albumId, item]),
    ).values(),
  );
  return explicit.length ? [explicit[rotation % explicit.length]] : [];
}
export function dividedFallbackCandidates(home = {}, rotation = 0, primaryId) {
  const candidates = uniqueBy(
    [
      'dividedOpinionReviews',
      'latestReviews',
      'featuredReviews',
      'recommendedReviews',
      'koreanMusicReviews',
      'globalMusicReviews',
    ]
      .flatMap((key) => home[key] || [])
      .filter(
        (item) => item.albumId !== primaryId && hasReviewText(item) && item.albumReviewCount >= 2,
      ),
    'albumId',
  );
  const offset = candidates.length ? rotation % candidates.length : 0;
  return [...candidates.slice(offset), ...candidates.slice(0, offset)].slice(0, 8);
}
